import React from "react";
import { Alert, Spinner } from "react-bootstrap";
import styled from "styled-components";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import { colors } from "utils";
import { sanitizeImHtml } from "./sanitizeImHtml";

// Content already uses MathJax's default TeX delimiters (`\( ... \)` /
// `\[ ... \]`), so no delimiter conversion is needed.
const mathJaxConfig = {
  loader: { load: ["input/tex", "output/chtml"] },
  tex: {
    inlineMath: [["\\(", "\\)"]],
    displayMath: [["\\[", "\\]"]],
  },
};

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ItemCard = styled.div`
  background: ${colors.white || "#FFFFFF"};
  border: 1px solid ${colors.nightlyWoods200 || "#E5E7EB"};
  border-radius: 8px;
  overflow: hidden;
`;

const ItemHeader = styled.div`
  background: ${colors.milkGlass200 || "#F3F4F6"};
  border-bottom: 1px solid ${colors.nightlyWoods200 || "#E5E7EB"};
  color: ${colors.nightlyWoods900 || "#111827"};
  font-size: 13px;
  font-weight: 600;
  padding: 10px 16px;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
`;

const Column = styled.div`
  padding: 16px;

  & + & {
    border-left: 1px solid ${colors.nightlyWoods200 || "#E5E7EB"};
  }
`;

const ColumnLabel = styled.div`
  color: ${colors.nightlyWoods700 || "#6B7280"};
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
  text-transform: uppercase;
`;

const Placeholder = styled.div`
  color: ${colors.nightlyWoods500 || "#9CA3AF"};
  font-style: italic;
`;

const Empty = styled.div`
  color: ${colors.nightlyWoods700 || "#6B7280"};
  padding: 32px;
  text-align: center;
`;

// curriculum_label, ordinal_name, and (when present) the 1-based problem
// number, e.g. "Practice Problems — Practice Problems — Problem 2".
const buildHeaderLabel = (item) => {
  const parts = [item.curriculum_label, item.ordinal_name].filter(Boolean);
  if (item.problem_index != null) parts.push(`Problem ${item.problem_index}`);
  return parts.join(" — ");
};

const RenderedSide = ({ html, emptyMessage }) => {
  if (!html) return <Placeholder>{emptyMessage}</Placeholder>;
  return (
    <MathJax dynamic>
      <div dangerouslySetInnerHTML={{ __html: sanitizeImHtml(html) }} />
    </MathJax>
  );
};

export const ItemsList = ({ items, loading, error, hasLesson }) => {
  if (loading) {
    return (
      <Empty>
        <Spinner animation="border" size="sm" /> Loading items...
      </Empty>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        {String(error?.detail || error?.message || error)}
      </Alert>
    );
  }

  if (!hasLesson) {
    return <Alert variant="info">Select a grade, unit, and lesson to browse items.</Alert>;
  }

  if (!items || items.length === 0) {
    return <Alert variant="info">No items found for this lesson and section type.</Alert>;
  }

  return (
    <MathJaxContext version={3} config={mathJaxConfig}>
      <List>
        {items.map((item) => (
          <ItemCard key={item.material_id}>
            <ItemHeader>{buildHeaderLabel(item)}</ItemHeader>
            <Columns>
              <Column>
                <ColumnLabel>Student</ColumnLabel>
                <RenderedSide html={item.student_html} emptyMessage="No student version" />
              </Column>
              <Column>
                <ColumnLabel>Teacher</ColumnLabel>
                <RenderedSide html={item.teacher_html} emptyMessage="No teacher version" />
              </Column>
            </Columns>
          </ItemCard>
        ))}
      </List>
    </MathJaxContext>
  );
};
